/**
 * Icon action buttons for the Appointment page header.
 */
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import {
  IconCalendarEvent,
  IconCopy,
  IconMicrophone,
  IconNotes,
  IconPencil,
  IconPlayerStop,
  IconVideo,
} from '@tabler/icons-react';

export interface AppointmentActionsClusterProps {
  onEdit: () => void;
  onOpenSession: () => void;
  /** Telehealth/hybrid opens the video call; in-person opens the session tools drawer. */
  openSessionAsVideo?: boolean;
  showOpenSession?: boolean;
  onCopyLink?: () => void;
  showCopyLink?: boolean;
  onViewCalendar: () => void;
  onStartRecording?: () => void;
  /** Force-end a stuck or active in-person recording session from the appointment page. */
  onEndRecording?: () => void;
  showRecordButton?: boolean;
  recordDisabled?: boolean;
  recordingInProgress?: boolean;
  endingRecording?: boolean;
  disabled?: boolean;
}

const HEADER_ACTION_ICON_PROPS = {
  variant: 'light' as const,
  color: 'gray' as const,
  size: 'lg' as const,
  radius: 'md' as const,
  bg: 'gray.3',
  styles: {
    root: {
      border: 'none',
      color: 'var(--mantine-color-gray-8)',
    },
  },
};

export function AppointmentActionsCluster({
  onEdit,
  onOpenSession,
  openSessionAsVideo = false,
  showOpenSession = true,
  onCopyLink,
  showCopyLink = false,
  onViewCalendar,
  onStartRecording,
  onEndRecording,
  showRecordButton = false,
  recordDisabled = false,
  recordingInProgress = false,
  endingRecording = false,
  disabled = false,
}: AppointmentActionsClusterProps): JSX.Element {
  return (
    <Group gap={6} wrap="nowrap" justify="flex-end">
      <Tooltip label="Edit appointment">
        <ActionIcon
          {...HEADER_ACTION_ICON_PROPS}
          disabled={disabled}
          onClick={onEdit}
          aria-label="Edit appointment"
          data-testid="appointment-action-edit"
        >
          <IconPencil size={18} />
        </ActionIcon>
      </Tooltip>
      {showRecordButton && (recordingInProgress ? onEndRecording : onStartRecording) ? (
        <Tooltip
          label={
            recordingInProgress ? 'End recording session' : 'Start in-person recording'
          }
        >
          <ActionIcon
            {...HEADER_ACTION_ICON_PROPS}
            disabled={disabled || recordDisabled || endingRecording}
            loading={endingRecording}
            onClick={recordingInProgress ? onEndRecording : onStartRecording}
            aria-label={
              recordingInProgress ? 'End in-person recording' : 'Start in-person recording'
            }
            data-testid="appointment-action-record"
          >
            {recordingInProgress ? <IconPlayerStop size={18} /> : <IconMicrophone size={18} />}
          </ActionIcon>
        </Tooltip>
      ) : null}
      {showOpenSession ? (
        <Tooltip label={openSessionAsVideo ? 'Join video call' : 'Open session'}>
          <ActionIcon
            {...HEADER_ACTION_ICON_PROPS}
            disabled={disabled}
            onClick={onOpenSession}
            aria-label={openSessionAsVideo ? 'Join video call' : 'Open session'}
            data-testid="appointment-action-open-session"
          >
            {openSessionAsVideo ? <IconVideo size={18} /> : <IconNotes size={18} />}
          </ActionIcon>
        </Tooltip>
      ) : null}
      {showCopyLink && onCopyLink ? (
        <Tooltip label="Copy session link">
          <ActionIcon
            {...HEADER_ACTION_ICON_PROPS}
            disabled={disabled}
            onClick={onCopyLink}
            aria-label="Copy session link"
            data-testid="appointment-action-copy-link"
          >
            <IconCopy size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <Tooltip label="View in calendar">
        <ActionIcon
          {...HEADER_ACTION_ICON_PROPS}
          disabled={disabled}
          onClick={onViewCalendar}
          aria-label="View in calendar"
          data-testid="appointment-action-calendar"
        >
          <IconCalendarEvent size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
